'use client'

import { useEffect } from 'react'

import { POST_MESSAGE_TYPES } from '~/lib/constants/post-message'

import { setAnalyticsConsent } from './client'

type ConsentMessage = {
  analytics?: boolean
  type?: string
}

const DEFAULT_PARENT_ORIGIN = 'https://todobarro.com'
const TRUSTED_PARENT_DOMAINS = ['todobarro.com', 'todobarro-sim.bules.eu']

function isTrustedParentOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin)

    return TRUSTED_PARENT_DOMAINS.some(domain =>
      hostname === domain || hostname.endsWith(`.${domain}`),
    )
  }
  catch {
    return false
  }
}

function resolveParentOrigin(): string {
  if (typeof document === 'undefined' || !document.referrer) {
    return DEFAULT_PARENT_ORIGIN
  }

  try {
    const referrerOrigin = new URL(document.referrer).origin

    return isTrustedParentOrigin(referrerOrigin)
      ? referrerOrigin
      : DEFAULT_PARENT_ORIGIN
  }
  catch {
    return DEFAULT_PARENT_ORIGIN
  }
}

function isConsentMessage(message: ConsentMessage | null): message is {
  analytics: boolean
  type: typeof POST_MESSAGE_TYPES.COOKIE_CONSENT
} {
  return message?.type === POST_MESSAGE_TYPES.COOKIE_CONSENT
    && typeof message.analytics === 'boolean'
}

export function PostHogConsentBridge(): null {
  useEffect(() => {
    if (window.parent === window) {
      return
    }

    const parentOrigin = resolveParentOrigin()

    function handleMessage(event: MessageEvent<ConsentMessage | null>): void {
      if (!isTrustedParentOrigin(event.origin)) {
        return
      }

      if (!isConsentMessage(event.data)) {
        return
      }
      setAnalyticsConsent(event.data.analytics)
    }

    window.addEventListener('message', handleMessage)
    window.parent.postMessage(
      {
        type: POST_MESSAGE_TYPES.COOKIE_CONSENT_READY,
      },
      parentOrigin,
    )

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  return null
}
